import { MouseEvent, type ReactNode, type Ref } from 'react'
import classNames from 'classnames/bind'
import { isEmpty } from 'ramda'

import styles from './td.module.scss'

/**
 * UI COMPONENTS v2
 *
 * Ячейка таблицы
 * ------
 * Семантически верная таблица. Ячейка таблицы (td)
 */

const cx = classNames.bind(styles)

export type TdProps = {
  children: ReactNode
  className?: string
  stripStyles?: boolean
  stripPadding?: boolean
  alignLeft?: boolean
  alignCenter?: boolean
  alignRight?: boolean
  verticalAlignMiddle?: boolean
  verticalAlignBottom?: boolean
  colSpan?: number
  rowSpan?: number
  reference?: Ref<HTMLTableCellElement | 'td'>
  headers?: string
  first?: boolean
  last?: boolean
  hidden?: boolean
  invisibility?: boolean
  bgFilled?: boolean
  bgColor?: string
  highlighted?: boolean
  bold?: boolean
  ignoreFirstChild?: boolean
  ignoreLastChild?: boolean
  borderLeft?: boolean
  borderRight?: boolean
  borderTop?: boolean
  style?: { [key: string]: string }
  width?: number
  minWidth?: number
  maxWidth?: number
  onMouseEnter?: (e: MouseEvent) => void
  onMouseLeave?: (e: MouseEvent) => void
  ignoreMouseEvents?: boolean
  onClick?: (e: MouseEvent) => void
}

const Td = ({
  children,
  className,
  stripStyles,
  stripPadding,
  alignLeft,
  alignCenter,
  alignRight,
  verticalAlignMiddle,
  verticalAlignBottom,
  colSpan,
  rowSpan,
  reference,
  headers,
  first,
  last,
  hidden,
  invisibility,
  bgFilled,
  bgColor,
  highlighted,
  bold,
  ignoreFirstChild,
  ignoreLastChild,
  borderLeft,
  borderRight,
  borderTop,
  style,
  width,
  minWidth,
  maxWidth,
  onMouseEnter,
  onMouseLeave,
  ignoreMouseEvents,
  onClick,
}: TdProps) => {
  const cn = {
    root: !stripStyles,
    rootPadding: !stripStyles && !stripPadding,
    alignLeft,
    alignCenter,
    alignRight,
    verticalAlignMiddle,
    verticalAlignBottom,
    first,
    last,
    hidden,
    invisibility,
    bgFilled,
    highlighted,
    bold,
    ignoreFirstChild,
    ignoreLastChild,
    borderLeft,
    borderRight,
    borderTop,
  }

  if (hidden) {
    return null
  }

  const styleOverall = {
    ...(style ?? {}),
    ...(width ? { width, maxWidth: width } : {}),
    ...(minWidth ? { minWidth } : {}),
    ...(maxWidth ? { maxWidth } : {}),
    ...(bgColor ? { backgroundColor: bgColor } : {}),
  }

  const mouseEvents = ignoreMouseEvents ? {} : { onMouseEnter, onMouseLeave, onClick }

  const props = {
    colSpan,
    rowSpan,
    headers,
    style: isEmpty(styleOverall) ? undefined : styleOverall,
    ...mouseEvents,
  }

  return (
    <td className={cx(cn, className)} ref={reference as Ref<HTMLTableCellElement>} {...props}>
      {children}
    </td>
  )
}

export default Td
